import { Component, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BodyFigureComponent } from 'src/app/shared/ui-common/body-figure/body-figure.component';
import { ButtonComponent } from 'src/app/shared/ui-common/button/button.component';
import { BODY_PARTS, BodyPainColor, BodyPart, BodyPartLabel } from 'src/app/shared/data/models/body-parts.model';
import { BodyPrefill, SurveySession } from '../../data/survey.model';
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';

@Component({
  selector: 'app-survey-body',
  imports: [CommonModule, BodyFigureComponent, ButtonComponent, TranslatePipe],
  templateUrl: './survey-body.component.html',
  styleUrl: './survey-body.component.scss',
})
export class SurveyBodyComponent {
  @Input() surveySession: SurveySession | null = null;
  @Input() bodyPrefill: BodyPrefill | null = null;
  @Input() isOnDemand = false;

  @Output() bodySurveyFinished = new EventEmitter<Record<BodyPartLabel, BodyPart> | null>();

  @ViewChild('bodyFigure') bodyFigure!: BodyFigureComponent;

  @HostListener('window:resize')
  onResize(): void {
    this.narrowScreenButtons = window.innerWidth <= 1000;
  }

  narrowScreenButtons = window.innerWidth <= 1000;
  selectedBodyPart: BodyPartLabel | null = null;
  bodyParts: Record<BodyPartLabel, BodyPart> = BODY_PARTS;
  hasPain = false;
  hasNoPainClicked = false;

  onResetAllClick(): void {
    this.bodyFigure.resetAllBodyParts();
    this.resetAllBodyParts();
    this.hasPain = false;
    this.hasNoPainClicked = false;
  }

  onBodyPartSelected(bodyPart: BodyPartLabel | null): void {
    this.selectedBodyPart = bodyPart;
  }

  onBodyPainChanged(bodyParts: Record<BodyPartLabel, BodyPart>): void {
    this.bodyParts = bodyParts;
    this.hasPain = Object.values(bodyParts).some(part => part.painLevel > 0);
    this.hasNoPainClicked = false;
  }

  onNoPainClick(): void {
    this.hasPain = false;
    this.resetAllBodyParts();
    this.hasNoPainClicked = true;
    this.bodyFigure.resetAllBodyParts();
    this.bodySurveyFinished.emit(this.bodyParts);
  }

  onRefillClick(): void {
    if (this.bodyPrefill) {
      this.bodyParts = this.mapBodyPrefillToBodyParts(this.bodyPrefill);
      this.hasPain = Object.values(this.bodyParts).some(part => part.painLevel > 0);
      this.bodyFigure.refillBodyParts(this.bodyParts);
    }
  }

  onNextBodyPartClick(): void {
    this.bodyFigure.nextBodyPart();
  }

  onSaveBodyPartPainClick(): void {
    this.bodyFigure.saveBodyPartPainClick();
  }

  onCompleteClick(): void {
    this.bodySurveyFinished.emit(this.bodyParts);
  }

  private resetAllBodyParts(): void {
    Object.keys(this.bodyParts).forEach((key: string) => {
      this.bodyParts[key as BodyPartLabel].painLevel = -1;
    });
  }

  private mapBodyPrefillToBodyParts(prefill: BodyPrefill): Record<BodyPartLabel, BodyPart> {
    return {
      [BodyPartLabel.BodyAnklesFeetLeft]: {
        painLevel: prefill.BodyAnklesFeetLeft ?? -1,
        color: this.getBodyPainColor(prefill.BodyAnklesFeetLeft ?? -1)
      },
      [BodyPartLabel.BodyAnklesFeetRight]: {
        painLevel: prefill.BodyAnklesFeetRight ?? -1,
        color: this.getBodyPainColor(prefill.BodyAnklesFeetRight ?? -1)
      },
      [BodyPartLabel.BodyElbowsLeft]: {
        painLevel: prefill.BodyElbowsLeft ?? -1,
        color: this.getBodyPainColor(prefill.BodyElbowsLeft ?? -1)
      },
      [BodyPartLabel.BodyElbowsRight]: {
        painLevel: prefill.BodyElbowsRight ?? -1,
        color: this.getBodyPainColor(prefill.BodyElbowsRight ?? -1)
      },
      [BodyPartLabel.BodyHipsThighs]: {
        painLevel: prefill.BodyHipsThighs ?? -1,
        color: this.getBodyPainColor(prefill.BodyHipsThighs ?? -1)
      },
      [BodyPartLabel.BodyKneesLeft]: {
        painLevel: prefill.BodyKneesLeft ?? -1,
        color: this.getBodyPainColor(prefill.BodyKneesLeft ?? -1)
      },
      [BodyPartLabel.BodyKneesRight]: {
        painLevel: prefill.BodyKneesRight ?? -1,
        color: this.getBodyPainColor(prefill.BodyKneesRight ?? -1)
      },
      [BodyPartLabel.BodyLowerback]: {
        painLevel: prefill.BodyLowerback ?? -1,
        color: this.getBodyPainColor(prefill.BodyLowerback ?? -1)
      },
      [BodyPartLabel.BodyNeck]: {
        painLevel: prefill.BodyNeck ?? -1,
        color: this.getBodyPainColor(prefill.BodyNeck ?? -1)
      },
      [BodyPartLabel.BodyShouldersLeft]: {
        painLevel: prefill.BodyShouldersLeft ?? -1,
        color: this.getBodyPainColor(prefill.BodyShouldersLeft ?? -1)
      },
      [BodyPartLabel.BodyShouldersRight]: {
        painLevel: prefill.BodyShouldersRight ?? -1,
        color: this.getBodyPainColor(prefill.BodyShouldersRight ?? -1)
      },
      [BodyPartLabel.BodyUpperback]: {
        painLevel: prefill.BodyUpperback ?? -1,
        color: this.getBodyPainColor(prefill.BodyUpperback ?? -1)
      },
      [BodyPartLabel.BodyWristsHandsLeft]: {
        painLevel: prefill.BodyWristsHandsLeft ?? -1,
        color: this.getBodyPainColor(prefill.BodyWristsHandsLeft ?? -1)
      },
      [BodyPartLabel.BodyWristsHandsRight]: {
        painLevel: prefill.BodyWristsHandsRight ?? -1,
        color: this.getBodyPainColor(prefill.BodyWristsHandsRight ?? -1)
      }
    };
  }

  private getBodyPainColor(painLevel: number): BodyPainColor {
    return BodyPainColor[`LEVEL_${painLevel}` as keyof typeof BodyPainColor] ?? BodyPainColor.NOPAIN;
  }
}
